import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Button,
  Modal,
  Typography,
  Form,
  TextInput,
} from '@la/ds-ui-components';
import { StaffInvitation } from '@la/types';
import { isValidEmail } from '@la/utilities';
import { InputGroupContainer } from 'domains/MemberRegistration/RegistrationInfoCard/RegistrationInfoCard.styles';
import { AddRosterWarningBanner } from '../AddRosterWarningBanner/AddRosterWarningBanner';
import { InviteStaffContent } from '../InvitePlayerStaffModals';
import * as S from './AddStaffModal.styles';

type AddStaffModalProps = {
  onSubmit: (values: AddStaffFormFields) => void;
  staffInviteLinks?: StaffInvitation[];
  isOpen: boolean;
  onOpenChange: () => void;
};

export type AddStaffFormFields = {
  firstName: string;
  lastName: string;
  email: string;
  roleId: string;
};

const FIELD_REQUIRED_ERROR = 'This field is required.';
const VALID_EMAIL_ADDRESS_ERROR = 'Please enter a valid email address.';
const ADD_STAFF_FORM_ID = 'add-staff-form';

const addStaffFormValidation = yup.object<AddStaffFormFields>({
  firstName: yup.string().trim().required(FIELD_REQUIRED_ERROR),
  lastName: yup.string().trim().required(FIELD_REQUIRED_ERROR),
  email: yup
    .string()
    .trim()
    .required(FIELD_REQUIRED_ERROR)
    .test('is-valid-email', VALID_EMAIL_ADDRESS_ERROR, isValidEmail),
});

export const AddStaffModal = ({
  onSubmit,
  staffInviteLinks,
  isOpen,
  onOpenChange,
}: AddStaffModalProps) => {
  const [roleId, setRoleId] = useState('');

  const defaultValues: AddStaffFormFields = {
    firstName: '',
    lastName: '',
    email: '',
    roleId: '',
  };

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(addStaffFormValidation),
  });

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch,
  } = methods;

  const handleSelectOption = (roleId: string) => {
    setRoleId(roleId);
  };

  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const email = watch('email');

  return (
    <Modal
      open={isOpen}
      onOpenChange={onOpenChange}
      title="Add a staff member"
      primaryAction={
        <Button type="submit" form={ADD_STAFF_FORM_ID} disabled={!roleId}>
          Add staff
        </Button>
      }
      tertiaryAction={
        <Button variant="text" size="medium">
          Cancel
        </Button>
      }
    >
      <AddRosterWarningBanner type="staff member" />
      <FormProvider {...methods}>
        <Form
          id={ADD_STAFF_FORM_ID}
          noValidate
          onSubmit={handleSubmit((values) => {
            if (!isSubmitting) {
              onSubmit({ ...values, roleId });
            }
          })}
        >
          <S.AddStaffFormContainer>
            <InputGroupContainer>
              <TextInput
                {...register('firstName')}
                errorMessage={errors.firstName?.message}
                hasError={!!errors.firstName}
                id="firstName"
                label="Staff member first name"
                required
                value={firstName}
              />

              <TextInput
                {...register('lastName')}
                errorMessage={errors.lastName?.message}
                hasError={!!errors.lastName}
                id="lastName"
                label="Staff member last name"
                required
                value={lastName}
              />
            </InputGroupContainer>

            <TextInput
              {...register('email')}
              errorMessage={errors.email?.message}
              hasError={!!errors.email}
              id="email"
              label="Email address"
              required
              value={email}
            />
          </S.AddStaffFormContainer>
          <S.AddStaffFormContainer>
            <Typography variant="ui" size="xl">
              Grab an invite link that you can share with your newly added staff
              members!
            </Typography>
            <InviteStaffContent
              onSelectOption={handleSelectOption}
              staffInvites={staffInviteLinks ?? []}
            />
          </S.AddStaffFormContainer>
        </Form>
      </FormProvider>
    </Modal>
  );
};
